<script setup>
import { onBeforeMount } from "vue";
import { useStore } from "vuex";
import { useAuthService } from "@/services/auth.service";
import { useI18n } from 'vue-i18n';

const store = useStore();
const { login } = useAuthService();
const { t } = useI18n();

onBeforeMount(() => {
  store.state.showNavbar = true;
  store.state.showSidenav = false;
  store.state.showFooter = true;
  store.state.layout = "landing";
  store.state.hideConfigButton = true;
});

const handleLogin = () => {
  login();
};

const handleSignup = () => {
  // Redirect to sign up page
  window.location.href = '/signup';
};
</script>

<template>
  <main class="landing-page">
    <!-- Hero Section -->
    <section class="hero-section landing-bg">
      <div class="container">
        <div class="row align-items-center min-vh-75">
          <div class="col-lg-6">
            <div class="hero-content text-white">
              <h1 class="display-4 fw-bold mb-4">
                {{ t('landing.hero.title') }} <span class="text-gradient">TerraSense</span>
              </h1>
              <p class="lead mb-4">
                {{ t('landing.hero.subtitle') }}
              </p>
              <div class="d-flex gap-3 flex-wrap">
                <button 
                  @click="handleSignup" 
                  class="btn btn-white btn-lg px-4 py-2"
                >
                  <i class="fas fa-user-plus me-2"></i>{{ t('landing.hero.get_started') }}
                </button>
                <button 
                  @click="handleLogin" 
                  class="btn btn-outline-light btn-lg px-4 py-2"
                >
                  <i class="fas fa-sign-in-alt me-2"></i>{{ t('landing.hero.sign_in') }}
                </button>
              </div>
            </div>
          </div>
          <div class="col-lg-6 text-center">
            <div class="hero-image">
              <img 
                src="https://terrasense.imgix.net/logos/terrasense.svg" 
                alt="TerraSense" 
                class="img-fluid hero-logo mb-4"
              >
              <div class="feature-grid">
                <div class="feature-card">
                  <div class="feature-icon">
                    <i class="fas fa-chart-line"></i>
                  </div>
                  <h6 class="text-white">{{ t('landing.hero.features.realtime_analytics') }}</h6>
                </div>
                <div class="feature-card">
                  <div class="feature-icon">
                    <i class="fas fa-shield-alt"></i>
                  </div>
                  <h6 class="text-white">{{ t('landing.hero.features.secure_monitoring') }}</h6>
                </div>
                <div class="feature-card">
                  <div class="feature-icon">
                    <i class="fas fa-cloud"></i>
                  </div>
                  <h6 class="text-white">{{ t('landing.hero.features.cloud_integration') }}</h6>
                </div>
                <div class="feature-card">
                  <div class="feature-icon">
                    <i class="fas fa-mobile-alt"></i>
                  </div>
                  <h6 class="text-white">{{ t('landing.hero.features.mobile_ready') }}</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-7 bg-white">
      <div class="container">
        <div class="row">
          <div class="col-12 text-center mb-5">
            <h2 class="display-6 fw-bold text-primary">{{ t('landing.features.title') }}</h2>
            <p class="lead text-muted">{{ t('landing.features.subtitle') }}</p>
          </div>
        </div>
        <div class="row g-4">
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 text-center border-0 shadow-sm">
              <div class="card-body p-4">
                <div class="feature bg-gradient-primary text-white rounded-circle mx-auto mb-3">
                  <i class="fas fa-tachometer-alt"></i>
                </div>
                <h5 class="card-title">{{ t('landing.features.dashboard_overview.title') }}</h5>
                <p class="card-text text-muted">
                  {{ t('landing.features.dashboard_overview.desc') }}
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 text-center border-0 shadow-sm">
              <div class="card-body p-4">
                <div class="feature bg-gradient-success text-white rounded-circle mx-auto mb-3">
                  <i class="fas fa-bell"></i>
                </div>
                <h5 class="card-title">{{ t('landing.features.smart_alerts.title') }}</h5>
                <p class="card-text text-muted">
                  {{ t('landing.features.smart_alerts.desc') }}
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 text-center border-0 shadow-sm">
              <div class="card-body p-4">
                <div class="feature bg-gradient-info text-white rounded-circle mx-auto mb-3">
                  <i class="fas fa-chart-pie"></i>
                </div>
                <h5 class="card-title">{{ t('landing.features.advanced_reports.title') }}</h5>
                <p class="card-text text-muted">
                  {{ t('landing.features.advanced_reports.desc') }}
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="card h-100 text-center border-0 shadow-sm">
              <div class="card-body p-4">
                <div class="feature bg-gradient-warning text-white rounded-circle mx-auto mb-3">
                  <i class="fas fa-cogs"></i>
                </div>
                <h5 class="card-title">{{ t('landing.features.easy_management.title') }}</h5>
                <p class="card-text text-muted">
                  {{ t('landing.features.easy_management.desc') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-7 bg-gradient-primary">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8">
            <h2 class="display-6 fw-bold text-white mb-4">
              {{ t('landing.cta.title') }}
            </h2>
            <p class="lead text-white mb-4">
              {{ t('landing.cta.subtitle') }}
            </p>
            <div class="d-flex gap-3 justify-content-center flex-wrap">
              <button 
                @click="handleSignup" 
                class="btn btn-white btn-lg px-5 py-3"
              >
                <i class="fas fa-rocket me-2"></i>{{ t('landing.cta.start_trial') }}
              </button>
              <button 
                @click="handleLogin" 
                class="btn btn-outline-light btn-lg px-5 py-3"
              >
                <i class="fas fa-sign-in-alt me-2"></i>{{ t('landing.cta.sign_in_now') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style scoped>
.landing-page {
  overflow-x: hidden;
}

.landing-bg {
  background: linear-gradient(135deg, #2E7D32 0%, #4A8C2A 50%, #66BB6A 100%);
  padding: 8rem 0 6rem 0;
  position: relative;
}

.landing-bg::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="40" r="0.8" fill="rgba(255,255,255,0.08)"/><circle cx="40" cy="80" r="1.2" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.hero-content {
  position: relative;
  z-index: 2;
}

.text-gradient {
  background: linear-gradient(45deg, #A5D6A7, #E8F5E8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-logo {
  max-width: 300px;
  opacity: 0.9;
  border-radius: 1rem;
  animation: bounce 1.2s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
    animation-timing-function: cubic-bezier(0.8,0,1,1);
  }
  50% {
    transform: translateY(-8px); /* Reduced bounce height */
    animation-timing-function: cubic-bezier(0,0,0.2,1);
  }
}

.feature-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
}

.feature-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.feature-icon {
  width: 3rem;
  height: 3rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.25rem;

  i {
    color: #fff!important;
  }
}

.feature {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 4rem;
  width: 4rem;
  font-size: 2rem;
  margin-bottom: 1rem;
}

/* Primary color overrides for TerraSense green theme */
.btn-primary {
  background: linear-gradient(45deg, #2E7D32, #4A8C2A);
  border: none;
  box-shadow: 0 4px 15px rgba(46, 125, 50, 0.3);
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: linear-gradient(45deg, #1B5E20, #2E7D32);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(46, 125, 50, 0.4);
}

.btn-outline-light:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.8);
}

.btn-white {
  background: #fff;
  color: #2E7D32;
  border: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-white:hover {
  background: #f8f9fa;
  color: #1B5E20;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.text-primary {
  color: #2E7D32 !important;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #2E7D32, #4A8C2A) !important;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #4A8C2A, #66BB6A) !important;
}

.bg-gradient-info {
  background: linear-gradient(135deg, #81C784, #A5D6A7) !important;
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #F9A825, #FFB74D) !important;
}

/* Card styling */
.card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .landing-bg {
    padding: 4rem 0 3rem 0;
  }
  
  .display-4 {
    font-size: 2.5rem;
  }
  
  .hero-logo {
    max-width: 200px;
    margin-bottom: 2rem;
  }
  
  .feature-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .feature-card {
    padding: 1rem;
  }
  
  .btn-lg {
    padding: 0.75rem 2rem;
    font-size: 1rem;
  }
  
  .py-7 {
    padding-top: 4rem !important;
    padding-bottom: 4rem !important;
  }
}

@media (max-width: 576px) {
  .d-flex.gap-3 {
    flex-direction: column;
    gap: 1rem !important;
  }
  
  .btn {
    width: 100%;
  }
  
  .display-6 {
    font-size: 1.75rem;
  }

  .hero-logo {
    margin-top: 2rem;
  }
}

/* Animation classes */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-content > * {
  animation: fadeInUp 0.8s ease-out;
}

.hero-content > *:nth-child(2) {
  animation-delay: 0.2s;
}

.hero-content > *:nth-child(3) {
  animation-delay: 0.4s;
}

.hero-content > *:nth-child(4) {
  animation-delay: 0.6s;
}

/* Add style for browser tag logo if needed */
.browser-tag-logo {
  display: inline-block;
  vertical-align: middle;
  height: 32px;
  width: auto;
}

/* Increase padding for Features and CTA sections */
.py-7 {
  padding-top: 6rem !important;
  padding-bottom: 6rem !important;
}
</style>